'use client'

import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { useIsMobile } from '@/hooks/use-mobile'
import { cn } from '@/lib/utils'
import { Hotel } from '@/payload-types'
import IconBadge from '../common/IconBadge'
import { IconName } from 'lucide-react/dynamic'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion'
import RichText from '../richText'
import { useTranslations } from 'next-intl'
import ContactInfo from '../common/ContactInfo'

export type AmenityContent = {
  title: string
  amenities: Hotel['amenities']
}

export function AmenitySheet({
  title,
  amenities,
  hotel,
}: {
  title: string
  amenities: Hotel['amenities']
  hotel: Hotel
}) {
  const t = useTranslations()

  const isMobile = useIsMobile()
  const infoAmenities = amenities?.filter((amenity) => amenity.info)
  const amenitiesSimple = amenities?.filter((amenity) => !amenity.info)

  return (
    <Sheet>
      <SheetTrigger className='text-left font-medium underline'>{t('hotel_info')}</SheetTrigger>
      <SheetContent className={cn('overflow-x-scroll', { 'w-screen': isMobile })}>
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
        </SheetHeader>

        <div className='space-y-8'>
          <ContactInfo hotel={hotel} />
          <span className='flex h-[1px] w-full rounded-full bg-stone-200' />
        </div>

        <div>
          {infoAmenities &&
            infoAmenities.map((amenity, index) => {
              if (typeof amenity.icon === 'number') return null

              return (
                <div key={index}>
                  {amenity.info && amenity.infoRichText && (
                    <Accordion
                      type='single'
                      collapsible
                      className='my-2 rounded-md bg-stone-100 px-2'
                    >
                      <AccordionItem value='item-1'>
                        <AccordionTrigger>
                          <div key={amenity.id} className='flex items-center space-x-2'>
                            <IconBadge
                              dynamic={amenity.icon.icon as IconName}
                              className='bg-transparent'
                            />
                            <p>{amenity.icon.title}</p>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <RichText data={amenity.infoRichText} />
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  )}
                </div>
              )
            })}
        </div>

        <div className='space-y-8'>
          {amenitiesSimple && amenitiesSimple.length > 0 && (
            <>
              <div className='space-y-4'>
                <h3>{t('amenities')}</h3>

                {amenitiesSimple.map((amenity) => {
                  if (typeof amenity.icon === 'number') return null

                  return (
                    <div key={amenity.id} className='flex items-center space-x-2 py-2'>
                      <IconBadge dynamic={amenity.icon.icon as IconName} />
                      <p>{amenity.icon.title}</p>
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
